import { createApp } from 'vue'; // Import createApp from 'vue'
import { createStore } from 'vuex'; // Import createStore from 'vuex'
import '../../utils/lightbox'; // Assuming this is side-effect import or utility setup
import { appCheck } from "../../utils/firebase/appCheck";
import { getToken } from "firebase/app-check";
import emitter from '../../utils/bus'; // Keeping custom bus for now
import { DestroyAttachment } from '../../utils/graphql/attachment-graph';
import { DestroyImage } from '../../utils/graphql/image-graph';
import graph_client from '../../utils/graphql/graph_client';
import {
  GetReview,
} from '../../utils/graphql/review-graph';
import {
  CreateComment,
  UpdateComment,
  DestroyComment,
  GetHikeComments,
  GetRecordingComments,
  GetHikeListComments,
  GetCommentsPage,
} from '../../utils/graphql/comments-graph';
import axiosClient from '../../utils/helpers/axios_client';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import { ratingTagSections } from '../../utils/models/RatingTag';
import App from './App.vue'; // Your root component
import { i18n } from '../../i18n'; // Import i18n

type OpenCommentDialogDetail = {
  recordingId?: string | number | null;
  parentComment?: any;
};

declare global {
  interface Window {
    openCommentDialog?: (event?: CustomEvent<OpenCommentDialogDetail>) => void;
    __reviewsOpenCommentListener?: (event: Event) => void;
  }
}

// Map the UI sort option to the backend `sort` argument.
// Backend supports: 'ranking_desc' (default), 'created_desc', 'created_asc', 'rating_desc'.
const SORT_PARAM_MAP: Record<string, string> = {
  ranking: 'ranking_desc',
  created: 'created_desc',
  rating: 'rating_desc',
};
const sortParamFor = (order: string): string => SORT_PARAM_MAP[order] || 'ranking_desc';

// --- Vuex 4 Store Definition ---
const store = createStore({
  state() { // state is now a function
    return {
      showModal: false,
      loading: false,
      newCommentLoading: false,
      hikeId: window.hike_id, // Still reading from global scope initially
      commentableType: window.commentable_type || 'Hike',
      editingComment: null,
      user: window.user, // Still reading from global scope initially
      hike: null,
      orderings: ['created', 'rating', 'ranking'],
      selectedOrder: 'ranking',
      body: '',
      rating: null, // Rating is now optional
      selectedTags: [], // Rating-based tags selected in the add-comment flow (tag keys)
      recordingId: null, // Recording is optional
      uploading: false,
      clientId: `client_${Math.random().toString(36).substr(2, 9)}`,
      files: [],
      photoUrls: [],
      uploadedImages: [], // Store image data from direct upload
      destroyingPhotoIds: [], // Consider if this needs to be reactive if UI depends on it during destruction
      pollingInterval: 3000, // Not used in the provided actions/getters, but kept
      error: null, // Added for error handling in fetchTrail
      replyingToComment: null, // Track which comment is being replied to
      commentsPagination: {
        totalCount: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      commentsPerPage: 10,
      loadingMoreComments: false,
    }
  },
  // --- Mutations ---
  // Mutations are synchronous functions responsible for *actually* changing state
  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    SET_SHOW_MODAL(state, value) {
      state.showModal = value;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_UPLOADING(state, value) {
      state.uploading = value;
    },
    SET_BODY(state, value) {
      state.body = value;
    },
    SET_RATING(state, value) {
      state.rating = value;
      // The selectable chips are derived solely from the new rating's catalog,
      // and the low-star (1-3) and high-star (4-5) tag sets are disjoint. Drop
      // any selected tags that aren't valid for the new rating so the user can
      // never submit stale tags they can no longer see or deselect.
      const validKeys = new Set(
        ratingTagSections(value).flatMap((s) => s.tags.map((t) => t.key))
      );
      state.selectedTags = state.selectedTags.filter((k) => validKeys.has(k));
    },
    SET_SELECTED_TAGS(state, tags) {
      state.selectedTags = Array.isArray(tags) ? [...tags] : [];
    },
    TOGGLE_TAG(state, tagKey) {
      if (state.selectedTags.includes(tagKey)) {
        state.selectedTags = state.selectedTags.filter((t) => t !== tagKey);
      } else {
        state.selectedTags = [...state.selectedTags, tagKey];
      }
    },
    SET_RECORDING_ID(state, value) {
      state.recordingId = value;
    },
    REMOVE_FILE(state, fileToRemove) {
      state.files = state.files.filter((f) => f.firebaseRefFullPath !== fileToRemove.firebaseRefFullPath);
    },
    ADD_DESTROYING_PHOTO_ID(state, photoId) {
      state.destroyingPhotoIds.push(photoId);
    },
    REMOVE_DESTROYING_PHOTO_ID(state, photoId) {
      state.destroyingPhotoIds = state.destroyingPhotoIds.filter(id => id !== photoId);
    },
    REMOVE_EDITING_COMMENT_PHOTO(state, photoId) {
      if (state.editingComment && state.editingComment.images) {
        state.editingComment.images = state.editingComment.images.filter((i) => i.id !== photoId);
      }
    },
    RESET_COMMENT_FORM_STATE(state) {
      state.body = '';
      state.rating = null; // Rating is optional
      state.selectedTags = []; // Clear rating-based tags
      state.recordingId = null; // Recording is optional
      state.loading = false; // Also reset loading here?
      state.files = [];
      state.photoUrls = [];
      state.uploadedImages = [];
      // DON'T clear replyingToComment here - it's set explicitly before resetState is called
      // state.replyingToComment = null;
      // Consider if destroyingPhotoIds should be reset here too
    },
    SET_HIKE(state, { commentable, comments, pagination }) {
      if (commentable) {
        state.hike = { ...commentable, comments: comments || [] };
      } else {
        state.hike = null;
      }

      const perPage = pagination?.perPage || state.commentsPerPage;
      const totalCount = pagination?.totalCount ?? (comments ? comments.length : 0);
      const totalPages = pagination?.totalPages ?? (perPage > 0 ? Math.max(1, Math.ceil(totalCount / perPage)) : 1);
      const currentPage = pagination?.currentPage ?? 1;

      state.commentsPerPage = perPage;

      state.commentsPagination = {
        totalCount,
        totalPages,
        currentPage,
        perPage,
        hasNextPage: pagination?.hasNextPage ?? currentPage < totalPages,
        hasPreviousPage: pagination?.hasPreviousPage ?? currentPage > 1,
      };

      if (state.hike) {
        state.hike.commentsCount = totalCount;
      }
    },
    SET_ERROR(state, errorMessage) {
      state.error = errorMessage;
    },
    SET_EDITING_COMMENT(state, comment) {
      state.editingComment = comment;
      if (comment) {
        state.body = comment.body;
        state.rating = comment.rating || null;
        state.selectedTags = Array.isArray(comment.tags) ? [...comment.tags] : [];
        const normalizedType = typeof comment.commentableType === 'string'
          ? comment.commentableType.toLowerCase()
          : '';
        const connectedRecordingId = comment.connectedRecordingId || null;

        state.recordingId = normalizedType === 'recording' ? null : connectedRecordingId;
        // Reset temporary upload state when starting edit
        state.photoUrls = [];
        state.files = [];
        state.uploadedImages = [];
      }
    },
    REMOVE_COMMENT_FROM_LIST(state, commentId) {
      if (!state.hike || !state.hike.comments) return;

      let removedTopLevel = false;

      const removeFromTree = (comments) => {
        return comments.filter(c => {
          if (String(c.id) === String(commentId)) {
            if (!c.parentCommentId) {
              removedTopLevel = true;
            }
            return false;
          }
          if (c.childComments && c.childComments.length > 0) {
            c.childComments = removeFromTree(c.childComments);
          }
          return true;
        });
      };

      state.hike.comments = removeFromTree(state.hike.comments);

      if (removedTopLevel) {
        const newTotalCount = Math.max(0, state.commentsPagination.totalCount - 1);
        const newTotalPages = state.commentsPagination.perPage > 0
          ? Math.max(1, Math.ceil(newTotalCount / state.commentsPagination.perPage))
          : state.commentsPagination.totalPages;
        const newCurrentPage = Math.min(state.commentsPagination.currentPage, newTotalPages);

        state.commentsPagination = {
          ...state.commentsPagination,
          totalCount: newTotalCount,
          totalPages: newTotalPages,
          currentPage: newCurrentPage,
          hasNextPage: newCurrentPage < newTotalPages,
          hasPreviousPage: newCurrentPage > 1,
        };

        if (state.hike) {
          state.hike.commentsCount = newTotalCount;
        }
      }
    },
    UPDATE_COMMENT_IN_LIST(state, updatedComment) {
      if (state.hike && state.hike.comments) {
        // Recursive function to update comment in tree
        const updateInTree = (comments) => {
          return comments.map(c => {
            if (c.id === updatedComment.id) {
              return { ...c, ...updatedComment };
            }
            if (c.childComments) {
              c.childComments = updateInTree(c.childComments);
            }
            return c;
          });
        };
        state.hike.comments = updateInTree(state.hike.comments);
      }
    },
    ADD_COMMENT_TO_LIST(state, newComment) {
      if (!state.hike) return;

      const normalizedComment = {
        ...newComment,
        childComments: newComment.childComments || [],
        childCount: newComment.childCount || 0,
        hasChildren: newComment.hasChildren || ((newComment.childComments || []).length > 0),
        likeCount: newComment.likeCount || (newComment.likes ? newComment.likes.length : 0),
      };

      if (!state.hike.comments) {
        state.hike.comments = [];
      }

      if (normalizedComment.parentCommentId) {
        const addToParent = (comments) => {
          return comments.map(c => {
            if (String(c.id) === String(normalizedComment.parentCommentId)) {
              if (!c.childComments) c.childComments = [];

              const existingIndex = c.childComments.findIndex(child => String(child.id) === String(normalizedComment.id));
              if (existingIndex !== -1) {
                c.childComments[existingIndex] = normalizedComment;
              } else {
                c.childComments.unshift(normalizedComment);
                c.childCount = (c.childCount || 0) + 1;
              }
              c.hasChildren = true;
            } else if (c.childComments && c.childComments.length > 0) {
              c.childComments = addToParent(c.childComments);
            }
            return c;
          });
        };

        state.hike.comments = addToParent(state.hike.comments);
      } else {
        const existingIndex = state.hike.comments.findIndex(c => String(c.id) === String(normalizedComment.id));
        if (existingIndex !== -1) {
          state.hike.comments[existingIndex] = normalizedComment;
        } else {
          state.hike.comments.unshift(normalizedComment);

          const newTotalCount = state.commentsPagination.totalCount + 1;
          const totalPages = state.commentsPagination.perPage > 0
            ? Math.max(1, Math.ceil(newTotalCount / state.commentsPagination.perPage))
            : state.commentsPagination.totalPages;

          state.commentsPagination = {
            ...state.commentsPagination,
            totalCount: newTotalCount,
            totalPages,
            hasNextPage: state.commentsPagination.currentPage < totalPages,
          };

          state.hike.commentsCount = newTotalCount;
        }
      }
    },
    REMOVE_COMMENT_FROM_LIST(state, commentId) {
      if (!state.hike || !state.hike.comments) return;

      const removeFromList = (comments) => {
        return comments.filter(c => {
          if (String(c.id) === String(commentId)) {
            return false; // Remove this comment
          }
          // Recursively remove from child comments
          if (c.childComments && c.childComments.length > 0) {
            c.childComments = removeFromList(c.childComments);
            c.childCount = c.childComments.length;
            c.hasChildren = c.childComments.length > 0;
          }
          return true;
        });
      };

      state.hike.comments = removeFromList(state.hike.comments);

      // Update pagination
      const newTotalCount = Math.max(0, state.commentsPagination.totalCount - 1);
      const totalPages = state.commentsPagination.perPage > 0
        ? Math.max(1, Math.ceil(newTotalCount / state.commentsPagination.perPage))
        : state.commentsPagination.totalPages;

      state.commentsPagination = {
        ...state.commentsPagination,
        totalCount: newTotalCount,
        totalPages,
        hasNextPage: state.commentsPagination.currentPage < totalPages,
      };
    },
    APPEND_COMMENTS(state, { comments, pagination }) {
      if (!state.hike) return;

      const existingIds = new Set((state.hike.comments || []).map(comment => String(comment.id)));
      const normalize = (comment) => ({
        ...comment,
        childComments: comment.childComments || [],
        childCount: comment.childCount || 0,
        hasChildren: comment.hasChildren || ((comment.childComments || []).length > 0),
        likeCount: comment.likeCount || (comment.likes ? comment.likes.length : 0),
      });

      const newComments = (comments || [])
        .filter(comment => !existingIds.has(String(comment.id)))
        .map(normalize);

      state.hike.comments = [...(state.hike.comments || []), ...newComments];

      if (pagination) {
        state.commentsPagination = {
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
          currentPage: pagination.currentPage,
          perPage: pagination.perPage,
          hasNextPage: pagination.hasNextPage,
          hasPreviousPage: pagination.hasPreviousPage,
        };

        state.hike.commentsCount = pagination.totalCount;
      }
    },
    SET_LOADING_MORE_COMMENTS(state, value) {
      state.loadingMoreComments = value;
    },
    UPDATE_PHOTO_IN_COMMENT(state, photo) {
      if (state.hike && state.hike.comments) {
        const comment = state.hike.comments.find((c) => parseInt(c.id) === parseInt(photo.recordId));
        if (comment && comment.images) {
          const index = comment.images.findIndex((i) => parseInt(i.id) === parseInt(photo.id));
          if (index !== -1) {
            comment.images.splice(index, 1, photo);
          }
        }
      }
    },
    SET_SELECTED_ORDER(state, order) {
      state.selectedOrder = order;
    },
    SET_REPLYING_TO_COMMENT(state, comment) {
      state.replyingToComment = comment;
    },
    SET_NEW_COMMENT_LOADING(state, value) {
      state.newCommentLoading = value;
    },
  },
  // --- Actions ---
  // Actions commit mutations and can contain asynchronous operations
  actions: {
    // Simple actions just commit mutations
    setUser({ commit }, user) {
      commit('SET_USER', user);
    },
    setShowModal({ commit, dispatch }, value) {
      if (value) {
        dispatch('resetState'); // Reset state when opening modal (if intended)
      } else {
        // Clear reply state when closing modal
        commit('SET_REPLYING_TO_COMMENT', null);
      }
      commit('SET_SHOW_MODAL', value);
    },
    setUploading({ commit }, value) {
      commit('SET_UPLOADING', value);
    },
    setBody({ commit }, value) {
      commit('SET_BODY', value);
    },
    setRating({ commit }, value) {
      commit('SET_RATING', value);
    },
    setSelectedTags({ commit }, tags) {
      commit('SET_SELECTED_TAGS', tags);
    },
    toggleTag({ commit }, tagKey) {
      commit('TOGGLE_TAG', tagKey);
    },
    setRecordingId({ commit, state }, value) {
      const normalizedType = typeof state.commentableType === 'string'
        ? state.commentableType.toLowerCase()
        : '';

      if (normalizedType === 'recording') {
        commit('SET_RECORDING_ID', null);
        return;
      }

      commit('SET_RECORDING_ID', value);
    },
    removeFile({ commit }, file) {
      commit('REMOVE_FILE', file);
    },
    resetState({ commit }) {
      commit('RESET_COMMENT_FORM_STATE');
    },
    setSelectedOrder({ commit, dispatch }, order) {
      commit('SET_SELECTED_ORDER', order);
      // Sorting is performed server-side, and pagination must stay consistent with
      // the selected order, so re-fetch from the first page whenever it changes.
      dispatch('fetchHike', { page: 1 });
    },

    // Actions with async logic
    destroyPhoto({ commit, state }, payload) {
      // Handle both old (photo object) and new ({ photo, commentId }) formats
      const photo = payload.photo || payload;

      commit('ADD_DESTROYING_PHOTO_ID', photo.id);
      graph_client.request(DestroyImage, {
        image: {
          id: photo.id
        }
      })
        .then((response) => {
          if (response && response.destroyImage) {
            commit('REMOVE_EDITING_COMMENT_PHOTO', response.destroyImage.id);
          } else {
            // If response is null or missing data, still remove using the photo id we have
            commit('REMOVE_EDITING_COMMENT_PHOTO', photo.id);
          }
        })
        .catch(error => {
          console.error("Error destroying photo:", error);
          // Optionally commit an error state or show a notification
        })
        .finally(() => {
          // Remove from the destroying list regardless of success/failure
          commit('REMOVE_DESTROYING_PHOTO_ID', photo.id);
        });
    },

    async fetchHike({ state, commit, dispatch }, { page = 1 } = {}) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null); // Reset error before fetching
      try {
        let variables;
        let response;
        const sort = sortParamFor(state.selectedOrder);

        if (state.commentableType === 'Recording') {
          variables = {
            page,
            perPage: state.commentsPerPage,
            sort,
            recordingId: state.hikeId
          };
          response = await graph_client.request(GetRecordingComments, variables);
          commit('SET_HIKE', {
            commentable: response.recording,
            comments: response.commentsConnection?.comments || [],
            pagination: response.commentsConnection,
          });
        } else if (state.commentableType === 'HikeList') {
          variables = {
            page,
            perPage: state.commentsPerPage,
            sort,
            hikeListId: state.hikeId
          };
          response = await graph_client.request(GetHikeListComments, variables);
          commit('SET_HIKE', {
            commentable: response.hikeList,
            comments: response.commentsConnection?.comments || [],
            pagination: response.commentsConnection,
          });
        } else {
          variables = {
            page,
            perPage: state.commentsPerPage,
            sort,
            hikeId: state.hikeId
          };
          response = await graph_client.request(GetHikeComments, variables);
          commit('SET_HIKE', {
            commentable: response.hike,
            comments: response.commentsConnection?.comments || [],
            pagination: response.commentsConnection,
          });
        }
        dispatch('checkCommentUrl'); // Check URL after hike data is loaded
      } catch (error) {
        console.error("Error fetching hike data:", error);
        commit('SET_ERROR', `An error occurred while fetching data. ${error}`);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async loadMoreComments({ state, commit }) {
      if (!state.commentsPagination.hasNextPage || state.loadingMoreComments) {
        return;
      }

      const nextPage = state.commentsPagination.currentPage + 1;

      commit('SET_LOADING_MORE_COMMENTS', true);
      commit('SET_ERROR', null);

      try {
        const response = await graph_client.request(GetCommentsPage, {
          commentableType: state.commentableType,
          commentableId: state.hikeId,
          sort: sortParamFor(state.selectedOrder),
          page: nextPage,
          perPage: state.commentsPerPage,
        });

        commit('APPEND_COMMENTS', {
          comments: response.commentsConnection?.comments || [],
          pagination: response.commentsConnection,
        });
      } catch (error) {
        console.error('Error loading more comments:', error);
        commit('SET_ERROR', `Failed to load more comments: ${error}`);
      } finally {
        commit('SET_LOADING_MORE_COMMENTS', false);
      }
    },

    checkCommentUrl({ state, commit }) {
      const url = new URL(window.location.href);
      const commentId = url.searchParams.get('editing_comment_id');

      if (commentId) {
        if (state.user) { // Use state.user instead of window.user
          commit('SET_LOADING', true); // Indicate loading for comment
          // Note: We might need a GetComment query if it doesn't exist
          // For now, we'll find the comment in the existing hike data
          if (state.hike && state.hike.comments) {
            const comment = state.hike.comments.find(c => c.id === commentId);
            if (comment) {
              // Ensure it belongs to the current user
              if (comment.user?.firebaseUserId === state.user?.firebaseUserId) {
                commit('SET_EDITING_COMMENT', comment);
                commit('SET_SHOW_MODAL', true);
              } else {
                console.warn("Attempted to edit comment belonging to another user.");
              }
            } else {
              console.warn(`Comment with ID ${commentId} not found.`);
            }
            commit('SET_LOADING', false);
          }
        } else {
          // launch onboarding modal if commentId exists but user is not logged in
          if (window.onBoardingVue && typeof window.onBoardingVue.launchFor === 'function') {
            window.onBoardingVue.launchFor(OnBoardingGoal.ReviewUpdate);
          } else {
            console.warn('onBoardingVue not available or launchFor method missing.');
          }
        }
      }
    },

    destroyComment({ state, commit }, comment) {
      // confirm if the user wants to remove the comment
      if (!confirm('Are you sure you want to delete this comment?')) {
        return;
      }

      commit('ADD_DESTROYING_PHOTO_ID', comment.id); // Reuse this state for visual feedback

      console.log('[destroyComment] Attempting to delete comment ID:', comment.id, 'Type:', typeof comment.id);

      graph_client.request(DestroyComment, { id: comment.id })
        .then((response) => {
          if (response.destroyComment.success) {
            console.log('[destroyComment] Successfully deleted comment ID:', comment.id);
            commit('REMOVE_COMMENT_FROM_LIST', comment.id);
            // If this comment was being edited, close the modal and reset state
            if (state.editingComment && state.editingComment.id === comment.id) {
              commit('SET_SHOW_MODAL', false);
              commit('RESET_COMMENT_FORM_STATE');
              commit('SET_EDITING_COMMENT', null);
            }
          } else {
            console.error("[destroyComment] Failed to destroy comment:", response.destroyComment.errors);
            alert(`Failed to delete comment: ${response.destroyComment.errors.join(', ')}`);
          }
        })
        .catch(error => {
          console.error("[destroyComment] Error destroying comment:", error);
          alert(`Error deleting comment. Please refresh the page and try again.`);
        })
        .finally(() => {
          commit('REMOVE_DESTROYING_PHOTO_ID', comment.id); // Clean up
        });
    },

    addComment({ state, commit, dispatch }, parentComment = null) {
      // User check should rely on state, which might be initially populated from window.user
      if (!state.user) {
        // launch onboarding modal
        if (window.onBoardingVue && typeof window.onBoardingVue.launchFor === 'function') {
          window.onBoardingVue.launchFor(OnBoardingGoal.Reviews);
        } else {
          console.warn('onBoardingVue not available or launchFor method missing.');
        }
      } else {
        commit('SET_EDITING_COMMENT', null); // Clear any previous edit state
        commit('SET_REPLYING_TO_COMMENT', parentComment); // Set parent comment if replying
        dispatch('resetState'); // Reset form fields
        commit('SET_SHOW_MODAL', true);
      }
    },

    replyToComment({ dispatch }, comment) {
      dispatch('addComment', comment);
    },

    async submitReply({ state, commit }, { parentCommentId, body }) {
      try {
        const params = {
          commentableId: state.hikeId,
          commentableType: state.commentableType,
          body: body,
          parentCommentId: parentCommentId
        };

        const response = await graph_client.request(CreateComment, params);
        const createdComment = response.createComment;

        // Add the new comment to the list without reloading
        commit('ADD_COMMENT_TO_LIST', createdComment);

        return createdComment;
      } catch (error) {
        console.error('Error submitting reply:', error);
        throw error;
      }
    },

    editComment({ commit }, comment) {
      commit('SET_EDITING_COMMENT', comment); // This mutation now also sets body/rating and resets files/photoUrls
      commit('SET_SHOW_MODAL', true);
    },

    updatePhoto({ commit }, photo) {
      // This action seems to be called *after* a photo is uploaded/processed elsewhere?
      // It just updates the photo data within the comment list in the state.
      commit('UPDATE_PHOTO_IN_COMMENT', photo);
    },

    async submitComment({ state, commit, dispatch }) {
      if (state.loading) return; // Prevent double submission

      commit('SET_NEW_COMMENT_LOADING', true); // Set loading state for the new comment submission
      commit('SET_LOADING', true);

      try {
        if (state.editingComment) {
          // --- Update existing comment ---
          // Extract signed IDs from uploaded images
          const imageSignedIds = state.uploadedImages.map(img => img.signedId || img.firebaseImageReference);
          
          const params = {
            id: state.editingComment.id,
            body: state.body,
            ...(state.rating !== null && { rating: state.rating }), // Only include rating if not null
            ...(imageSignedIds.length > 0 && { imageSignedIds }), // Only include if there are new images
            tagList: state.selectedTags, // Always send so cleared tags persist
          };
          const response = await graph_client.request(UpdateComment, params);
          const updatedComment = response.updateComment.comment;

          // Update the comment in the state instead of reloading
          commit('UPDATE_COMMENT_IN_LIST', updatedComment);
        } else {
          // --- Create new comment ---
          // Extract signed IDs from uploaded images
          const imageSignedIds = state.uploadedImages.map(img => img.signedId || img.firebaseImageReference);
          
          const normalizedType = typeof state.commentableType === 'string'
            ? state.commentableType.toLowerCase()
            : '';

          const connectedRecordingId = normalizedType !== 'recording' && state.recordingId
            ? state.recordingId
            : null;

          const params = {
            commentableId: state.hikeId,
            commentableType: state.commentableType,
            body: state.body,
            ...(state.rating !== null && { rating: state.rating }), // Only include rating if not null
            ...(imageSignedIds.length > 0 && { imageSignedIds }), // Only include if there are images
            ...(state.selectedTags.length > 0 && { tagList: state.selectedTags }), // Rating-based tags
            ...(state.replyingToComment && { parentCommentId: state.replyingToComment.id }), // Include parent if replying
            ...(connectedRecordingId ? { connectedRecordingId } : {}),
          };
          const response = await graph_client.request(CreateComment, params);
          const createdComment = response.createComment;
          createdComment.fresh = true; // Add transient flag if needed by UI
          createdComment.checkForPhotos = imageSignedIds.length > 0; // Add transient flag

          // Add the new comment to the list
          commit('ADD_COMMENT_TO_LIST', createdComment);

          // Change sort order to show the new comment first
          commit('SET_SELECTED_ORDER', 'created');

          // Only reload if there are images, to ensure they're processed
          if (imageSignedIds.length > 0) {
            setTimeout(() => {
              dispatch('fetchHike'); // Refresh data instead of full page reload
            }, 3000);
          }
        }

        // Close modal and reset form state on success
        commit('SET_SHOW_MODAL', false);
        dispatch('resetState'); // Use dispatch to call resetState action
        commit('SET_EDITING_COMMENT', null); // Clear editing state
        commit('SET_REPLYING_TO_COMMENT', null); // Clear reply state

      } catch (error) {
        console.error("Error submitting comment:", error);
        commit('SET_ERROR', `Failed to submit comment: ${error}`);
        // Potentially show error to user in the modal
      } finally {
        commit('SET_LOADING', false);
        commit('SET_NEW_COMMENT_LOADING', false); // Clear the loading dialog
      }
    },
    // setSelectedOrder action already defined above
    
  },
  // --- Getters ---
  getters: {
    // Make sure state.hike and state.hike.comments exist before sorting
    orderedComments: (state) => {
      if (!state.hike || !state.hike.comments) {
        return []; // Return empty array if data isn't loaded yet
      }
      // Sorting is performed server-side (see `sortParamFor` / the `sort` argument),
      // so simply preserve the order returned by the backend. We only filter out
      // nested replies, which are rendered within their parent thread.
      return state.hike.comments.filter(comment => !comment.parentCommentId);
    },
    
    // Get all comments including nested ones for counting
    allComments: (state) => {
      if (!state.hike || !state.hike.comments) return [];
      
      const flattenComments = (comments) => {
        return comments.reduce((acc, comment) => {
          acc.push(comment);
          if (comment.childComments) {
            acc.push(...flattenComments(comment.childComments));
          }
          return acc;
        }, []);
      };
      
      return flattenComments(state.hike.comments);
    },
    // Getter for the GraphQL URL remains the same logic
    createGetHikeCommentsUrl: (state) => {
      if (state.commentableType === 'Recording') {
        const query = 'query GetRecordingComments($recordingId: ID!, $page: Int!, $perPage: Int!) { recording(id: $recordingId) { id title averageRating commentsCount ratingsCount } commentsConnection: comments(commentableType: "Recording", commentableId: $recordingId, page: $page, perPage: $perPage) { comments { id body bodyLocale rating createdAt updatedAt parentCommentId user { id firebaseUserId displayName avatar { lgUrl smUrl } } images { id file { id lgUrl smUrl mdUrl } } likes { id user { firebaseUserId } } childComments: paginatedChildComments { id body bodyLocale rating createdAt updatedAt parentCommentId user { id firebaseUserId displayName avatar { smUrl } } } } totalCount totalPages currentPage perPage hasNextPage hasPreviousPage } }';
        const variables = {
          recordingId: state.hikeId,
          page: state.commentsPagination.currentPage,
          perPage: state.commentsPagination.perPage,
        };
        return `/graphql?query=${encodeURIComponent(query)}&variables=${encodeURIComponent(JSON.stringify(variables))}`;
      }
      const query = 'query GetHikeComments($hikeId: ID!, $page: Int!, $perPage: Int!) { hike(id: $hikeId) { id name averageRating commentsCount ratingsCount } commentsConnection: comments(commentableType: "Hike", commentableId: $hikeId, page: $page, perPage: $perPage) { comments { id body bodyLocale rating createdAt updatedAt parentCommentId user { id firebaseUserId displayName avatar { lgUrl smUrl } } images { id file { id lgUrl smUrl mdUrl } } likes { id user { firebaseUserId } } childComments: paginatedChildComments { id body bodyLocale rating createdAt updatedAt parentCommentId user { id firebaseUserId displayName avatar { smUrl } } } } totalCount totalPages currentPage perPage hasNextPage hasPreviousPage } }';
      const variables = {
        hikeId: state.hikeId,
        page: state.commentsPagination.currentPage,
        perPage: state.commentsPagination.perPage,
      };
      return `/graphql?query=${encodeURIComponent(query)}&variables=${encodeURIComponent(JSON.stringify(variables))}`;
    },
    // Add getters for computed states if needed by components
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    isModalShown: (state) => state.showModal,
  },
});

// --- Vue 3 App Initialization ---
const app = createApp(App);

// Use the Vuex store
app.use(store);

// Use i18n
app.use(i18n);

const isRecordingCommentable = () =>
  typeof store.state.commentableType === 'string'
  && store.state.commentableType.toLowerCase() === 'recording';

const openCommentDialog = (event?: CustomEvent<OpenCommentDialogDetail>) => {
  const detail = event?.detail || {};

  const { recordingId = null, parentComment = null } = detail;

  setTimeout(() => {
    store.dispatch('addComment', parentComment ?? null);

    if (!isRecordingCommentable()) {
      store.dispatch('setRecordingId', recordingId ?? null);
    }
  }, 0);
};

if (!window.__reviewsOpenCommentListener) {
  window.__reviewsOpenCommentListener = (event: Event) => {
    openCommentDialog(event as CustomEvent<OpenCommentDialogDetail>);
  };

  document.addEventListener('reviews:open-comment-dialog', window.__reviewsOpenCommentListener);
}
window.openCommentDialog = openCommentDialog;

// --- Global Event Bus Listener (Vue 3 style) ---
// Still relies on the external 'bus' and global window.axiosClient
// Consider refactoring bus logic into the store or using provide/inject if possible
emitter.on('user:updated', async () => {
  console.log("User updated event received"); // Debug log
  try {
    // Ensure axiosClient is correctly initialized
    if (!axiosClient) {
      console.error("axiosClient is not initialized.");
      return;
    }
    const response = await axiosClient.get('/user/current');
    const user = response.data;
    window.user = user; // Update global if other non-Vue parts rely on it
    // Dispatch action to update user in store
    store.dispatch('setUser', user);

    // Check the comment URL again now that the user is potentially logged in
    if (store.state.user) { // Check store state
      store.dispatch('checkCommentUrl');
    }
  } catch (error) {
    console.error("Error fetching updated user:", error);
    store.commit('SET_ERROR', `Failed to update user: ${error}`);
  }
});


// Mount the app to the DOM
app.mount('#comments');

// Expose the app instance globally IF absolutely necessary (generally discouraged)
// window.vueReviewsApp = app;
// window.vueReviewsStore = store;
